import { json } from '@remix-run/node'
import { useLoaderData, useNavigate } from '@remix-run/react'
import { Flex } from '#app/components/layout/flex.tsx'
import { Link } from '#app/components/navigation/link.tsx'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Source, type componentProps, Styling, Usage, Features } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Link',
}

const component: componentProps = {
	name: 'Link',
	shortName: 'Link',
	fileName: 'link',
	category: 'navigation',
	description: ['A link allows a user to navigate to a page inside or outside the application.'],
	features: [
		'Supports client-side routing.',
		'Supports external links.',
		'Supports fragment identifiers.',
		'Disabled links are also supported.',
		'Consistent cross-browser hover, press, and keyboard focus states.',
		'Implemented as a custom ARIA link when handled via JavaScript.',
		'More semantic and intuitive API with <Link to> instead of <a href>.',
	],
	parts: [
		{
			name: 'Link',
			description: 'A link allows a user to navigate to a page inside or outside the application.',
		},
	],
	usage: `<Flex orientation="vertical" gap="10">
	<Flex items="baseline" gap="10" wrap>
		<Link to="/docs/components/media/icon">Default (typographical)</Link>
		<Link to="/docs/components/media/icon" variant="typographical">Typographical</Link>
		<Link to="/docs/components/media/icon" variant="unstyled">Unstyled</Link>
		<Link to="/docs/components/media/icon" isDisabled>Disabled</Link>
		<Link onPress={() => navigate('/docs/components/media/icon')}>JavaScript link</Link>
		<Link to="https://www.youtube.com/watch?v=dQw4w9WgXcQ">External link</Link>
	</Flex>
	<Flex items="baseline" gap="10" wrap>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="primary" size="sm">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="primary" size="md">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="primary" size="lg">Link styled as button</Link>
	</Flex>
	<Flex items="baseline" gap="10" wrap>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="secondary" size="sm">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="secondary" size="md">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="secondary" size="lg">Link styled as button</Link>
	</Flex>
	<Flex items="baseline" gap="10" wrap>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="danger" size="sm">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="danger" size="md">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="danger" size="lg">Link styled as button</Link>
	</Flex>
	<Flex items="baseline" gap="10" wrap>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="ghost" size="sm">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="ghost" size="md">Link styled as button</Link>
		<Link to="/docs/components/media/icon" variant="button" buttonVariant="ghost" size="lg">Link styled as button</Link>
	</Flex>
</Flex>`,
	styling: `.link {
	@apply rounded-sm;

	&.typographical {
		@apply underline;
	}
	&.unstyled {
		/* Purposefully empty */
	}
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const LinkRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, usage, shortName, fileName, styling } = component
	const navigate = useNavigate()

	return (
		<>
			<Description name={name} description={description} />
			<Usage usage={usage} shortName={shortName} />
			<Flex orientation="vertical" gap="10">
				<Flex items="baseline" gap="10" wrap>
					<Link to="/">Default (typographical)</Link>
					<Link to="/" variant="typographical">Typographical</Link>
					<Link to="/" variant="unstyled">Unstyled</Link>
					<Link to="/" isDisabled>Disabled</Link>
					<Link onPress={() => navigate('/')}>JavaScript link</Link>
					<a href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" className="link">External link</a>
					<Link to="#features">Fragment identifier</Link>
				</Flex>
				<Flex items="baseline" gap="10" wrap>
					<Link to="/" variant="button" buttonVariant="primary" size="sm">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="primary" size="md">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="primary" size="lg">Link styled as button</Link>
				</Flex>
				<Flex items="baseline" gap="10" wrap>
					<Link to="/" variant="button" buttonVariant="secondary" size="sm">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="secondary" size="md">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="secondary" size="lg">Link styled as button</Link>
				</Flex>
				<Flex items="baseline" gap="10" wrap>
					<Link to="/" variant="button" buttonVariant="danger" size="sm">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="danger" size="md">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="danger" size="lg">Link styled as button</Link>
				</Flex>
				<Flex items="baseline" gap="10" wrap>
					<Link to="/" variant="button" buttonVariant="ghost" size="sm">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="ghost" size="md">Link styled as button</Link>
					<Link to="/" variant="button" buttonVariant="ghost" size="lg">Link styled as button</Link>
				</Flex>
			</Flex>
			<Features features={component.features} />
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default LinkRoute
